import { useState, useEffect } from 'preact/hooks';
import { UserPlus, X, User, Users, RefreshCw, Loader2, Plus, MessageSquare } from 'lucide-preact';
import { isAddContactModalOpenSignal, showToast } from '../../state/ui.store';
import { waContactsSignal, activeWaFilterSignal, loadContacts, selectContact, contactsSignal } from '../../state/contacts.store';
import { apiFetch } from '../../services/api';
import type { WhatsAppContactSync, Contact } from '../../types/contact';
import { normalizeText, formatPhoneNumber } from '../../utils/formatters';

export function AddContactModal() {
  const isOpen = isAddContactModalOpenSignal.value;
  const [tab, setTab] = useState<'wa' | 'manual'>('wa');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [displayLimit, setDisplayLimit] = useState(50);

  // Manual Form State
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualNotes, setManualNotes] = useState('');

  const activeFilter = activeWaFilterSignal.value;

  useEffect(() => {
    if (isOpen && tab === 'wa') {
      setDisplayLimit(50);
      loadWhatsAppContacts();
    }
  }, [isOpen, activeFilter, tab]);

  const loadWhatsAppContacts = async () => {
    setLoading(true);
    try {
      const isGroupMode = activeFilter === 'groups';
      const endpoint = isGroupMode ? '/contacts/whatsapp-groups' : '/contacts/whatsapp-sync';
      const data = await apiFetch<WhatsAppContactSync[]>(endpoint);
      waContactsSignal.value = data;
    } catch (err: any) {
      showToast(`Erro ao carregar lista do WhatsApp: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMonitoring = async (name: string, phone: string, notes: string = '', jid?: string, isGroup: boolean = false, profilePicUrl?: string | null) => {
    try {
      await apiFetch('/contacts', {
        method: 'POST',
        body: JSON.stringify({ name, phone_number: phone, notes, jid, is_group: isGroup, profile_pic_url: profilePicUrl })
      });
      await loadContacts();
      const added = contactsSignal.value.find((c: Contact) => c.phone_number === phone.replace(/\D/g, ''));
      if (added) {
        selectContact(added);
      }
      showToast('Contato adicionado ao monitoramento!', 'success');
      isAddContactModalOpenSignal.value = false;
    } catch (err: any) {
      showToast(`Erro ao adicionar contato: ${err.message}`, 'error');
    }
  };

  const handleManualSubmit = (e: Event) => {
    e.preventDefault();
    if (!manualName || !manualPhone) {
      showToast('Por favor, preencha nome e telefone.', 'error');
      return;
    }
    handleAddMonitoring(manualName, manualPhone, manualNotes);
  };

  if (!isOpen) return null;

  const monitoredPhones = new Set(contactsSignal.value.map(c => c.phone_number));

  const normQuery = normalizeText(search);
  const queryWords = normQuery.split(/\s+/).filter(Boolean);

  const filteredWaContacts = waContactsSignal.value.filter(c => {
    if (queryWords.length === 0) return true;
    const normName = normalizeText(c.name || '');
    const normNumber = c.number || '';
    return queryWords.every(w => normName.includes(w) || normNumber.includes(w));
  });

  const visibleWaContacts = filteredWaContacts.slice(0, displayLimit);

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
      <div
        class="w-full max-w-lg p-6 bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl space-y-5"
        style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}
      >
        <div class="flex items-center justify-between border-b border-slate-800 pb-3">
          <h3 class="text-sm font-bold text-slate-100 flex items-center gap-2">
            <UserPlus class="w-4 h-4 text-cyan-400" />
            Monitorar Novo Contato
          </h3>
          <button onClick={() => isAddContactModalOpenSignal.value = false} class="text-slate-400 hover:text-white p-1 cursor-pointer" aria-label="Fechar">
            <X class="w-4 h-4" />
          </button>
        </div>

        {/* Tab switcher */}
        <div class="flex items-center gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setTab('wa')}
            class={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
              tab === 'wa' ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-400/40' : 'text-slate-200 hover:bg-slate-800'
            }`}
          >
            <MessageSquare class="w-3.5 h-3.5" />
            Selecionar do WhatsApp
          </button>
          <button
            onClick={() => setTab('manual')}
            class={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
              tab === 'manual' ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-400/40' : 'text-slate-200 hover:bg-slate-800'
            }`}
          >
            <UserPlus class="w-3.5 h-3.5" />
            Cadastro Manual
          </button>
        </div>

        {tab === 'wa' && (
          <div class="space-y-3">
            {/* Sub-filters */}
            <div class="flex items-center gap-2 border-b border-slate-800 pb-2">
              <button
                onClick={() => activeWaFilterSignal.value = 'contacts'}
                class={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  activeFilter === 'contacts' ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-400/40' : 'text-slate-300 hover:text-white'
                }`}
              >
                <User class="w-3.5 h-3.5" />
                Contatos Individuais
              </button>
              <button
                onClick={() => activeWaFilterSignal.value = 'groups'}
                class={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  activeFilter === 'groups' ? 'bg-purple-500/20 text-purple-100 border border-purple-400/40' : 'text-slate-300 hover:text-white'
                }`}
              >
                <Users class="w-3.5 h-3.5" />
                Grupos do WhatsApp
              </button>
            </div>

            <div class="flex items-center justify-between gap-2">
              <input
                type="text"
                placeholder="Buscar nome ou número..."
                value={search}
                onInput={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setDisplayLimit(50);
                }}
                class="flex-1 px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
              <button
                onClick={() => loadWhatsAppContacts()}
                class="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition cursor-pointer flex items-center justify-center"
                title="Recarregar"
                aria-label="Recarregar"
              >
                <RefreshCw class={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div class="h-64 overflow-y-auto space-y-1.5 pr-1">
              {loading ? (
                <div class="p-6 text-center text-xs text-slate-300 flex items-center justify-center gap-2">
                  <Loader2 class="w-4 h-4 animate-spin text-cyan-400" />
                  Carregando...
                </div>
              ) : visibleWaContacts.length === 0 ? (
                <div class="p-6 text-center text-xs text-slate-300">
                  Nenhum contato encontrado.
                </div>
              ) : (
                <>
                  {visibleWaContacts.map(c => {
                    const isMonitored = monitoredPhones.has(c.number);
                    const isGroup = c.is_group;

                    return (
                      <div key={c.id} class="p-3 rounded-2xl bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-3 hover:bg-slate-800/80 transition">
                        <div class="flex items-center gap-3 min-w-0">
                          <div class={`w-9 h-9 rounded-full ${isGroup ? 'bg-purple-950 text-purple-300 border border-purple-500/30' : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'} font-bold text-xs flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                            {c.profilePicUrl ? (
                              <img
                                src={c.profilePicUrl}
                                alt={c.name}
                                referrerPolicy="no-referrer"
                                class="w-full h-full object-cover rounded-full"
                                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                              />
                            ) : isGroup ? (
                              <Users class="w-4 h-4 text-purple-300" />
                            ) : (
                              c.name ? c.name.charAt(0).toUpperCase() : '?'
                            )}
                          </div>
                          <div class="min-w-0">
                            <h4 class="text-xs sm:text-sm font-bold text-slate-100 truncate tracking-tight">{c.name || 'Contato WhatsApp'}</h4>
                            <p class="text-[11px] font-mono text-slate-400">{isGroup ? `ID: ${c.number}` : formatPhoneNumber(c.number)}</p>
                          </div>
                        </div>
                        <div>
                          {isMonitored ? (
                            <span class="px-2.5 py-1 rounded-xl text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                              Já Monitorado
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAddMonitoring(c.name, c.number, isGroup ? 'Grupo WA' : 'Contato WA', c.id, isGroup, c.profilePicUrl)}
                              class="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-md shadow-cyan-600/20 flex items-center gap-1.5"
                            >
                              <Plus class="w-3.5 h-3.5" />
                              Monitorar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filteredWaContacts.length > visibleWaContacts.length && (
                    <div class="p-2 text-center">
                      <button
                        onClick={() => setDisplayLimit(prev => prev + 50)}
                        class="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold cursor-pointer transition"
                      >
                        Carregar mais ({filteredWaContacts.length - visibleWaContacts.length} restantes)...
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'manual' && (
          <form onSubmit={handleManualSubmit} class="space-y-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Nome Completo</label>
              <input
                type="text"
                placeholder="Ex: Carlos Silva"
                value={manualName}
                onInput={(e) => setManualName((e.target as HTMLInputElement).value)}
                class="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Número de Telefone (DDD + Número)</label>
              <input
                type="text"
                placeholder="Ex: 5511999999999"
                value={manualPhone}
                onInput={(e) => setManualPhone((e.target as HTMLInputElement).value)}
                class="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Notas Internas</label>
              <textarea
                rows={2}
                placeholder="Contexto..."
                value={manualNotes}
                onInput={(e) => setManualNotes((e.target as HTMLTextAreaElement).value)}
                class="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
            </div>
            <div class="pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => isAddContactModalOpenSignal.value = false}
                class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold shadow-md shadow-cyan-600/20 flex items-center gap-1.5 cursor-pointer"
              >
                <UserPlus class="w-4 h-4" />
                Cadastrar Contato
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
